import { Trans } from '@lingui/macro'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import {
  LARGE_MEDIA_BREAKPOINT,
  MAX_WIDTH_MEDIA_BREAKPOINT,
  MEDIUM_MEDIA_BREAKPOINT,
  SMALL_MEDIA_BREAKPOINT,
} from 'components/Tokens/constants'
import { LoadingBubble } from 'components/Tokens/loading'
import { sortAscendingAtom, sortMethodAtom, useSetSortMethod } from 'components/Tokens/state'
import { MouseoverTooltip } from 'components/Tooltip'
import { SupportedChainId } from 'constants/chains'
import { useAtomValue } from 'jotai/utils'
import { CSSProperties, ForwardedRef, forwardRef, ReactNode } from 'react'
import { Info } from 'react-feather'
import styled, { css, useTheme } from 'styled-components/macro'

import { IPoolOrPair } from '.'

enum TokenSortMethod {
  TRADES_COUNT = 'Trades Count',
  ATV = 'ATV',
  TOTAL_VOLUME = 'Total Volume',
  VOLUME = 'Volume (24hrs)',
  SYMBOL = 'Symbol',
  NAME = 'Token name',
}

const Cell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`
const StyledTokenRow = styled.div<{
  first?: boolean
  last?: boolean
  $loading?: boolean
}>`
  background-color: transparent;
  display: grid;
  font-size: 16px;
  grid-template-columns: 1fr 21fr;
  line-height: 24px;
  max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT};
  ${({ first, last }) => css`
    height: ${first || last ? '72px' : '64px'};
    padding-top: ${first ? '8px' : '0px'};
    padding-bottom: ${last ? '8px' : '0px'};
  `}
  padding-left: 12px;
  padding-right: 12px;
  transition: ${({
    theme: {
      transition: { duration, timing },
    },
  }) => css`background-color ${duration.medium} ${timing.ease}`};
  width: 100%;
  transition-duration: ${({ theme }) => theme.transition.duration.fast};

  &:hover {
    ${({ $loading, theme }) =>
      !$loading &&
      css`
        background-color: ${theme.hoverDefault};
      `}
    ${({ last }) =>
      last &&
      css`
        border-radius: 0px 0px 8px 8px;
      `}
  }

  @media only screen and (max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT}) {
    grid-template-columns: 1fr 22.3fr;
  }

  @media only screen and (max-width: ${LARGE_MEDIA_BREAKPOINT}) {
    grid-template-columns: 1fr 22.7fr;
  }

  @media only screen and (max-width: ${MEDIUM_MEDIA_BREAKPOINT}) {
    grid-template-columns: 1fr 21.2fr;
  }

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    /* grid-template-columns: 10fr; */
    /* min-width: unset; */
    border-bottom: 0.5px solid ${({ theme }) => theme.backgroundModule};
    :last-of-type {
      border-bottom: none;
    }
  }
`

const ClickableContent = styled.div`
  display: flex;
  text-decoration: none;
  color: ${({ theme }) => theme.textPrimary};
  align-items: center;
  cursor: pointer;
`
const ClickableName = styled(ClickableContent)`
  gap: 8px;
  max-width: 100%;
`
const StyledHeaderRow = styled(StyledTokenRow)`
  border-bottom: 1px solid;
  border-color: ${({ theme }) => theme.backgroundOutline};
  border-radius: 8px 8px 0px 0px;
  color: ${({ theme }) => theme.textTertiary};
  font-size: 14px;
  height: 48px;
  line-height: 16px;
  padding: 0px 12px;
  width: 100%;
  justify-content: center;

  &:hover {
    background-color: transparent;
  }

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    justify-content: space-between;
  }
`

const ListNumberCell = styled(Cell)<{ header: boolean }>`
  color: ${({ theme }) => theme.textTertiary};
  min-width: 32px;
  font-size: 14px;

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    display: none;
  }
`
const DataCell = styled(Cell)<{ sortable: boolean }>`
  justify-content: flex-end;
  min-width: 80px;
  user-select: ${({ sortable }) => (sortable ? 'none' : 'unset')};
  transition: ${({
    theme: {
      transition: { duration, timing },
    },
  }) => css`background-color ${duration.medium} ${timing.ease}`};
`

const NameCell = styled(Cell)`
  justify-content: flex-start;
  padding: 0px 8px;
  min-width: 240px;
  gap: 8px;
`

const HeaderCellWrapper = styled.span<{ textAlign?: 'left' | 'right' | 'center' }>`
  align-items: center;
  display: flex;
  gap: 4px;
  justify-content: ${({ textAlign }) =>
    textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center'};
  width: 100%;
`

const FeeTierWrapper = styled.div`
  width: fit-content;
  padding: 2px;
  border-radius: 5px;
  font-size: 13px;
  font-weight: 600;
  /* color: ${({ theme }) => theme.black}; */
  background-color: ${({ theme }) => (theme.darkMode ? 'rgb(64, 68, 79)' : '#BCC0CE')};
`

// const SparkLineCell = styled(Cell)`
//   padding: 0px 24px;
//   min-width: 120px;

//   @media only screen and (max-width: ${MAX_WIDTH_MEDIA_BREAKPOINT}) {
//     display: none;
//   }
// `
// const SparkLine = styled(Cell)`
//   width: 124px;
//   height: 42px;
// `
const StyledLink = styled.a`
  text-decoration: none;
`
const TokenInfoCell = styled(Cell)`
  gap: 8px;
  line-height: 24px;
  font-size: 16px;
  max-width: inherit;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    justify-content: flex-start;
    flex-direction: column;
    gap: 0px;
    width: max-content;
    font-weight: 500;
  } */
`
const TokenName = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
`
const TokenSymbol = styled(Cell)`
  display: none;
  color: ${({ theme }) => theme.textTertiary};
  text-transform: uppercase;

  @media only screen and (max-width: ${SMALL_MEDIA_BREAKPOINT}) {
    display: flex;
    font-size: 12px;
    height: 16px;
    justify-content: flex-start;
    width: 100%;
  }
`
const VolumeCell = styled(DataCell)`
  padding-right: 8px;
  justify-content: center;
  @media only screen and (max-width: ${LARGE_MEDIA_BREAKPOINT}) {
    display: none;
  }
`
const SmallLoadingBubble = styled(LoadingBubble)`
  width: 25%;
`
const MediumLoadingBubble = styled(LoadingBubble)`
  width: 65%;
`
const LongLoadingBubble = styled(LoadingBubble)`
  width: 90%;
`
const IconLoadingBubble = styled(LoadingBubble)`
  border-radius: 50%;
  width: 24px;
`
export const SparkLineLoadingBubble = styled(LongLoadingBubble)`
  height: 4px;
`

const InfoIconContainer = styled.div`
  margin-left: 2px;
  display: flex;
  align-items: center;
  cursor: help;
`

const HEADER_DESCRIPTIONS: Record<TokenSortMethod, ReactNode | undefined> = {
  [TokenSortMethod.TRADES_COUNT]: undefined,
  [TokenSortMethod.ATV]: <Trans>Average trade volume in each transaction. ATV = total volume / trades count</Trans>,
  [TokenSortMethod.SYMBOL]: undefined,
  [TokenSortMethod.NAME]: undefined,
  [TokenSortMethod.TOTAL_VOLUME]: <Trans>Total volume from the beginning 01.03.2024</Trans>,
  [TokenSortMethod.VOLUME]: undefined,
}

/* Get singular header cell for header row */
function HeaderCell({
  category,
  textAlign = 'right',
}: {
  category: TokenSortMethod // TODO: change this to make it work for trans
  textAlign?: 'left' | 'right' | 'center'
}) {
  const theme = useTheme()
  const sortAscending = useAtomValue(sortAscendingAtom)
  //@ts-ignore
  const handleSortCategory = useSetSortMethod(category)
  const sortMethod = useAtomValue(sortMethodAtom)

  const description = HEADER_DESCRIPTIONS[category]

  return (
    <HeaderCellWrapper textAlign={textAlign}>
      {/* @ts-ignore */}

      {category}
      {description && (
        <MouseoverTooltip text={description} placement="right">
          <InfoIconContainer>
            <Info size={14} />
          </InfoIconContainer>
        </MouseoverTooltip>
      )}
    </HeaderCellWrapper>
  )
}

/* Token Row: skeleton row component */
function TokenRow({
  header,
  listNumber,
  tokenInfo,
  // sparkLine,
  ...rest
}: {
  first?: boolean
  header: boolean
  listNumber?: ReactNode
  loading?: boolean
  tokenInfo: ReactNode
  last?: boolean
  style?: CSSProperties
}) {
  const rowCells = (
    <>
      <ListNumberCell header={header}>{listNumber}</ListNumberCell>
      <NameCell data-testid="name-cell">{tokenInfo}</NameCell>
      {/* <SymbolCell data-testid="symbol-cell" sortable={header}>
        {symbol}
      </SymbolCell>
      <VolumeCell data-testid="volume-cell" sortable={header}>
        {volume}
      </VolumeCell>
      <PriceCell data-testid="price-cell" sortable={header}>
        {price}
      </PriceCell>
      <PercentChangeCell data-testid="percent-change-cell" sortable={header}>
        {percentChange}
      </PercentChangeCell>
      <TvlCell data-testid="tvl-cell" sortable={header}>
        {tvl}
      </TvlCell>
      <SparkLineCell>{sparkLine}</SparkLineCell> */}
    </>
  )
  if (header) return <StyledHeaderRow data-testid="header-row">{rowCells}</StyledHeaderRow>
  return <StyledTokenRow {...rest}>{rowCells}</StyledTokenRow>
}

/* Header Row: top header row component for table */
export function PoolAndPairHeaderRow({ category }: { category: TokenSortMethod }) {
  return <TokenRow header={true} listNumber="#" tokenInfo={<HeaderCell category={category} textAlign="left" />} />
}

/* Loading State: row component with loading bubbles */
export function LoadingPoolAndPairRow(props: { first?: boolean; last?: boolean }) {
  return (
    <TokenRow
      header={false}
      listNumber={<SmallLoadingBubble />}
      tokenInfo={
        <>
          <IconLoadingBubble />
          <MediumLoadingBubble />
        </>
      }
      // symbol={<LoadingBubble />}
      // price={<MediumLoadingBubble />}
      // percentChange={<LoadingBubble />}
      // tvl={<LoadingBubble />}
      // volume={<LoadingBubble />}
      // sparkLine={<SparkLineLoadingBubble />}
      {...props}
    />
  )
}

interface LoadedRowProps {
  tokenListIndex: number
  tokenListLength: number
  // sparklineMap: SparklineMap
  sortRank: number
  data: IPoolOrPair
}

/* Loaded State: row component with token information */
export const PoolAndPairRow = forwardRef(
  (
    { tokenListIndex, tokenListLength, sortRank, data: { token0, token1, feeTier, id } }: LoadedRowProps,
    ref: ForwardedRef<HTMLDivElement>
  ) => {
    return (
      <div ref={ref} data-testid={`token-table-row-${id}`}>
        <StyledLink
          target="_blank"
          href={feeTier ? `https://info.xspswap.finance/#/pools/${id}` : `https://analytics.xspswap.finance/pair/${id}`}
        >
          <TokenRow
            header={false}
            listNumber={sortRank}
            tokenInfo={
              <ClickableName>
                {/* <QueryTokenLogo token={{ ...token, chain: Chain.Xinfin, address: token.id }} /> */}
                {/* <DoubleCurrencyLogo currency0={token0.id} currency1={token1.id} size={24} margin={true} /> */}
                <DoubleCurrencyLogo
                  currency0={{
                    ...token0,
                    chainId: SupportedChainId.XDC,
                    //@ts-ignore
                    wrapped: {
                      address: token0.id,
                    },
                    address: token0.id,
                    isNative: false,
                    isToken: true,
                  }}
                  currency1={{
                    ...token1,
                    chainId: SupportedChainId.XDC,
                    //@ts-ignore
                    wrapped: {
                      address: token1.id,
                    },
                    address: token1.id,
                    isNative: false,
                    isToken: true,
                  }}
                  size={24}
                  margin={true}
                />

                <TokenInfoCell>
                  <TokenName data-cy="token-name">{`${token0.symbol}/${token1.symbol}`}</TokenName>
                  {feeTier && <FeeTierWrapper>{`${feeTier / 10000}%`}</FeeTierWrapper>}
                </TokenInfoCell>
              </ClickableName>
            }
            first={tokenListIndex === 0}
            last={tokenListIndex === tokenListLength - 1}
          />
        </StyledLink>
      </div>
    )
  }
)

PoolAndPairRow.displayName = 'PoolAndPairRow'
